import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { setDrawingMode } from '@actions/map-actions';
import { downloadCSVExport } from '@actions/data-table-actions';
import { boundsToWKT } from '@utils/map-utils';
import { textColor, dotmapsBlue } from '@constants/colors';
import DrawLineIcon from '@icons/draw-line-icon';
import DrawCircleIcon from '@icons/draw-circle-icon';
import MeasurementIcon from '@icons/measurement-icon';
import { getToolbarConfig, getCurrentMapFilters, getCurrentFilterToggles, getCurrentViewport } from '@selectors/map-selector';
import { getCurrentEntityTypes } from '@selectors/entities-selector';
import ActionDownloadIconButton from '@shared/action-download-icon-button';
import MapToolbarIconButton from '@shared/map-toolbar-icon-button';
import { Icon, Tooltip } from '@mui';
import Separator from './separator';
import './tools.scss';

const Tools = () => {
  const dispatch = useDispatch();

  const mode = useSelector(state => state.map.drawing.mode);
  const toolsConfig = useSelector(getToolbarConfig);
  const mapFilters = useSelector(getCurrentMapFilters);
  const mapFilterToggles = useSelector(getCurrentFilterToggles);
  const { bounds: mapViewportBounds } = useSelector(getCurrentViewport);
  const entityTypes = useSelector(getCurrentEntityTypes);

  const filters = useMemo(() => {
    const result = {};
    Object.keys(entityTypes).forEach(type => {
      if (Object.prototype.hasOwnProperty.call(mapFilterToggles, type) && mapFilterToggles[type]) {
        result[type] = {
          ...mapFilters,
          ...entityTypes[type].filters,
          geom_in: boundsToWKT(mapViewportBounds)
        };
      }
    });
    return result;
  }, [entityTypes, mapFilters, mapFilterToggles, mapViewportBounds]);

  const setDrawingModeHandler = useCallback(drawingMode => {
    dispatch(setDrawingMode(mode === drawingMode ? '' : drawingMode));
  }, [dispatch, mode]);

  const setMeasurement = useCallback(() => {
    setDrawingModeHandler('measurement');
  }, [setDrawingModeHandler]);

  const setPolygon = useCallback(() => {
    setDrawingModeHandler('polygon');
  }, [setDrawingModeHandler]);

  const setCircle = useCallback(() => {
    setDrawingModeHandler('circle');
  }, [setDrawingModeHandler]);

  const exportCSV = useCallback(() => {
    Object.keys(filters).forEach(filter => {
      dispatch(downloadCSVExport(filter, null, filters[filter]));
    });
  }, [dispatch, filters]);

  const { measurement = true, polygon = true, circle = true } = toolsConfig;

  return (
    <div data-testid="map-tools" styleName="map-tools">
      {measurement && (
        <MapToolbarIconButton onClick={setMeasurement} tooltip="Measure distance">
          <MeasurementIcon color={mode === 'measurement' ? dotmapsBlue : textColor} />
        </MapToolbarIconButton>
      )}
      {polygon && (
        <MapToolbarIconButton onClick={setPolygon} tooltip="Select an area">
          <DrawLineIcon color={mode === 'polygon' ? dotmapsBlue : textColor} />
        </MapToolbarIconButton>
      )}
      {circle && (
        <MapToolbarIconButton onClick={setCircle} tooltip="Select an area">
          <DrawCircleIcon color={mode === 'circle' ? dotmapsBlue : textColor} />
        </MapToolbarIconButton>
      )}
      <Separator />
      {/* A plain <a>, not MapToolbarIconButton - the Link is already the real, labeled
          interactive element here; nesting a real <button> inside an <a> is invalid
          (nested-interactive). The sizing/centering class goes directly on the Link itself
          (not a further nested span) to match the sibling buttons' box exactly. */}
      <Tooltip title="Print">
        <Link to={'/map/print'} aria-label="Print" styleName="print-icon">
          <Icon color={textColor} aria-hidden="true">print</Icon>
        </Link>
      </Tooltip>
      <ActionDownloadIconButton color={textColor} loaderColor={dotmapsBlue} onClick={exportCSV} toolbar />
    </div>
  );
};

export default Tools;
